import type { MaybePromise } from '@amzn/vinyl-util'
import {
    AbortError,
    bufferToByteStr,
    createShortUid,
    Deferred,
    logDebug,
    logVerbose,
    type RequestInitOptions,
    requestWithRetry,
} from '@amzn/vinyl-util'
import type { AuthTokenWithRefresh } from '@amzn/vinyl-amzn'
import { windowEvents } from '@/util/interaction'
import { openWindow } from '@/util/openWindow'
import { ctx } from '@/util/context'

const { target } = ctx('oAuth2')

export type OAuthOptions = {
    readonly clientId: string
    readonly authorizationEndpoint: string
    readonly tokenEndpoint: string
    readonly scopes: readonly string[]
    readonly clientSecret: MaybePromise<string>
    readonly redirectUri: string
}

/**
 * Grant type values used in dynamic client registration and auth requests.
 *
 * @see [Appendix A.10](https://tools.ietf.org/html/rfc6749#appendix-A.10)
 */
export enum GrantType {
    /**
     * Used for exchanging an authorization code for one or more tokens.
     */
    AUTHORIZATION_CODE = 'authorization_code',

    /**
     * Used when obtaining an access token.
     */
    IMPLICIT = 'implicit',

    /**
     * Used when exchanging a refresh token for a new token.
     */
    REFRESH_TOKEN = 'refresh_token',

    /**
     * Used for client credentials flow.
     */
    CLIENT_CREDENTIALS = 'client_credentials',
}

function base64UrlEncode(str: string) {
    return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Hash the code verifier to generate the code challenge
 * @param verifier
 */
async function generateCodeChallenge(verifier: string) {
    const encoder = new TextEncoder()
    const data = encoder.encode(verifier)
    const hash = await crypto.subtle.digest('SHA-256', data)
    const base64Hash = bufferToByteStr(hash)
    return base64UrlEncode(base64Hash)
}

export async function createAuthorizeUrl(
    options: OAuthOptions
): Promise<{ readonly codeVerifier: string; readonly url: string }> {
    const codeVerifier = createShortUid()
    const codeChallenge = await generateCodeChallenge(codeVerifier)
    const params = new URLSearchParams({
        client_id: options.clientId,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        redirect_uri: options.redirectUri,
        response_type: 'code',
        scope: options.scopes.join(' '),
        state: codeVerifier,
    })
    return {
        codeVerifier,
        url: `${options.authorizationEndpoint}?${params.toString()}`,
    }
}

export async function oAuthAuthorize(options: OAuthOptions): Promise<string> {
    logDebug(target, 'oAuthAuthorize')
    const authorizeUrlPromise = createAuthorizeUrl(options)
    const popup = await openWindow(
        authorizeUrlPromise.then((r) => r.url),
        'authPopup',
        'width=400,height=600'
    )
    if (!popup) throw new Error('auth popup could not be opened')
    const { codeVerifier } = await authorizeUrlPromise
    sessionStorage.setItem('codeVerifier', codeVerifier)

    const authTokenPromise = new Deferred<string>()
    const sub = windowEvents.on(
        'message',
        (event) => {
            logVerbose(target, 'message from popup', event.data)
            const done = () => {
                popup.close()
                sub()
            }
            if (popup.closed || event.data.code === null) {
                authTokenPromise.reject(new AbortError())
                done()
            }
            if (event.data.code) {
                authTokenPromise.resolve(event.data.code)
                done()
            }
        },
        {}
    )

    const loadStartHandler = (event: Event) => {
        const url = new URL((event as any).url)
        const code = url.searchParams.get('code')!
        if (code) {
            authTokenPromise.resolve(code)
            popup.close()
            popup.removeEventListener('loadstart', loadStartHandler)
        }
    }
    popup.addEventListener('loadstart', loadStartHandler)

    return authTokenPromise
}

const commonInitOptions: RequestInitOptions = {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
}

/**
 * Exchange authorization code for access token
 */
export async function exchangeCodeForToken(
    options: OAuthOptions,
    code: string
): Promise<AuthTokenWithRefresh> {
    logDebug(target, 'exchangeCodeForToken')
    const codeVerifier = sessionStorage.getItem('codeVerifier')
    if (!codeVerifier) throw new Error('codeVerifier not set')
    const params = new URLSearchParams({
        code,
        client_id: options.clientId,
        client_secret: await options.clientSecret,
        redirect_uri: options.redirectUri,
        code_verifier: codeVerifier,
        grant_type: GrantType.AUTHORIZATION_CODE,
    })
    const response = await requestWithRetry(options.tokenEndpoint, {
        ...commonInitOptions,
        body: params,
    })
    const tokenData = await response.json()
    return tokenWithRefreshToAuthToken(tokenData)
}

export async function refreshToken(
    options: OAuthOptions,
    refreshToken: string
): Promise<AuthTokenWithRefresh> {
    logDebug(target, 'refreshToken')
    const params = new URLSearchParams({
        grant_type: GrantType.REFRESH_TOKEN,
        refresh_token: refreshToken,
        client_id: options.clientId,
        client_secret: await options.clientSecret,
    })
    const response = await requestWithRetry(options.tokenEndpoint, {
        ...commonInitOptions,
        body: params,
    })
    const tokenData = await response.json()
    return tokenWithRefreshToAuthToken(tokenData)
}

/**
 * Converts a TokenWithRefresh object from Amazon auth to have an expiry timestamp instead of a relative duration.
 *
 * @param token
 */
function tokenWithRefreshToAuthToken(token: any): AuthTokenWithRefresh {
    return {
        accessToken: token.access_token,
        expiresAt: parseInt(token.expires_in) * 1000 + Date.now(),
        refreshToken: token.refresh_token,
    }
}
